import { GridColDef } from '@mui/x-data-grid';
import { GeoJSONFeature } from 'ol/format/GeoJSON';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { SEVERITY_LABELS } from '../../../constants/map.constants';
import {
    DeviceDetailProperties,
    DeviceStatus,
    Parameter,
    ParameterRow,
    StatusSeverity,
} from '../../../models/models';
import { getDeviceStatusStart } from '../../../store/device/device.action';
import DataTable from '../../ui/data-table/data-table.component';

const DeviceTable = ({
    device,
    rowsSelected,
    onRowSelected,
    onDeviceStatusSeverityChange,
}: {
    device: GeoJSONFeature;
    rowsSelected: string[];
    onRowSelected: (row: ParameterRow) => void;
    onDeviceStatusSeverityChange: (status: StatusSeverity) => void;
}) => {
    const [currentDevice, setCurrentDevice] = useState<GeoJSONFeature>(device);
    const dispatch = useDispatch();

    const columns: GridColDef[] = [
        { field: 'name', headerName: 'Nome Parametro', flex: 0.4 },
        { field: 'value', headerName: 'Valore Attuale', flex: 0.4 },
        {
            field: 'status',
            headerName: 'Stato',
            flex: 0.2,
            cellClassName: (params) => {
                return `cell-status-${params.value}`;
            },
            valueFormatter: (value) => {
                return SEVERITY_LABELS[value] ?? '-';
            },
        },
    ];

    const getValue = (
        key: string,
        parameter: Parameter,
        oldStatus: DeviceStatus
    ) => {
        if (!oldStatus) return null;
        const type = parameter.type;
        if (oldStatus[key] != null) {
            if (type === 'number') {
                const weight = parameter.weight ?? 1;
                return (oldStatus[key] as number) * weight;
            } else if (type === 'enum' || type === 'label') {
                return oldStatus[key];
            }
        }
        return null;
    };

    const getValueLabel = (key: string, parameter: Parameter) => {
        const status = (currentDevice.properties as DeviceDetailProperties)
            .details.status;
        if (!status) return '-';
        const type = parameter.type;
        if (status[key] != null) {
            if (type === 'number') {
                const weight = parameter.weight ?? 1;
                return (
                    (status[key] as number) * weight +
                    (parameter.um ? ' ' + parameter.um : '')
                );
            } else if (type === 'enum') {
                const value = parameter.values![status[key]];
                return value.length > 0
                    ? value[0]
                    : status[key]?.toString() ?? '-';
            } else if (type === 'label') {
                return status[key]?.toString() ?? '-';
            }
        }
        return '-';
    };

    const getStatus = useCallback(
        (key: string, parameter: Parameter, currentStatus: DeviceStatus) => {
            const value = getValue(key, parameter, currentStatus) as number;
            if (value == null) return null;
            if (parameter.type === 'label') return null;
            const weight = parameter.weight ?? 1;
            if (
                parameter.error_high == null &&
                parameter.error_low == null &&
                parameter.warn_high == null &&
                parameter.warn_low == null
            ) {
                return null;
            }
            var status = 0;
            if (
                parameter.warn_high != null &&
                value >= parameter.warn_high * weight
            ) {
                status = 100;
            }
            if (
                parameter.warn_low != null &&
                value <= parameter.warn_low * weight
            ) {
                status = 100;
            }
            if (
                parameter.error_high != null &&
                value >= parameter.error_high * weight
            ) {
                status = 200;
            }
            if (
                parameter.error_low != null &&
                value <= parameter.error_low * weight
            ) {
                status = 200;
            }
            return status as StatusSeverity;
        },
        []
    );

    const getRows = () => {
        const properties = currentDevice.properties as DeviceDetailProperties;
        const parameters =
            properties.details.configuration.definition.parameters;
        var rows: ParameterRow[] = [];
        Object.keys(parameters).forEach((key) => {
            rows.push({
                id: key,
                name: parameters[key].caption,
                type: parameters[key].type,
                weight: parameters[key].weight,
                um: parameters[key].um,
                values: parameters[key].values,
                warn_high: parameters[key].warn_high,
                warn_low: parameters[key].warn_low,
                error_high: parameters[key].error_high,
                error_low: parameters[key].error_low,
                value: getValueLabel(key, parameters[key]),
                status: getStatus(
                    key,
                    parameters[key],
                    properties.details.status
                ),
            });
        });
        return rows;
    };

    useEffect(() => {
        if (device) {
            const intervalId = setInterval(() => {
                const deviceId = device.id;
                dispatch(
                    getDeviceStatusStart(deviceId, (status) => {
                        setCurrentDevice((prev: GeoJSONFeature) => {
                            if (!prev) return prev;
                            const newValue = Object.assign({}, prev);
                            let currentStatus: StatusSeverity = 0;
                            Object.keys(status).forEach((key) => {
                                const properties =
                                    prev.properties as DeviceDetailProperties;
                                const parameter =
                                    properties.details.configuration.definition
                                        .parameters[key];
                                const paramStatus =
                                    getStatus(key, parameter, status) ?? 0;
                                currentStatus =
                                    paramStatus > currentStatus
                                        ? paramStatus
                                        : currentStatus;
                            });
                            onDeviceStatusSeverityChange(currentStatus);
                            (
                                newValue.properties as DeviceDetailProperties
                            ).details.status = status;
                            return newValue;
                        });
                    })
                );
            }, 5000);
            return () => clearInterval(intervalId);
        }
    }, [dispatch, device, onDeviceStatusSeverityChange, getStatus]);

    return (
        <DataTable
            rows={getRows()}
            columns={columns}
            onRowSelected={onRowSelected}
            rowsSelectedIds={rowsSelected}
            noRowSelection
        />
    );
};

export default DeviceTable;
